// Auto-pageview listener — dispara `instance.page()` em navegação SPA
// (pushState/replaceState), back/forward (popstate), e hashchange.
//
// Por que monkey-patch em vez de addEventListener("pushstate")? History API
// não dispara eventos nativamente para pushState/replaceState — só popstate.
// Sem monkey-patch, SPAs que usam pushState (React Router, Vue Router, etc.)
// não gerariam pageview automático. Esse patch é padrão (GA4, Mixpanel usam
// o mesmo).
//
// Opt-in via `window.__ETS_CONFIG__.auto_page`. Default: true. Desative para
// apps que chamam page() manualmente ou não querem auto-tracking de SPA.

import type { ETS } from "./ets.js";

interface HistoryMethod {
  (data: unknown, unused: string, url?: string | URL | null): void;
}

let installed = false;

/**
 * Instala listeners de navegação. Chama `instance.page()` em:
 * - history.pushState / history.replaceState (SPAs)
 * - popstate (back/forward do browser)
 * - hashchange (links com #fragment)
 *
 * Idempotente — segunda chamada é no-op. Não remove o patch ao destruir ETS
 * porque a intenção é "instalado uma vez por página"; se o cenário mudar e
 * tivermos múltiplas instâncias ETS no mesmo page, revisitar.
 */
export function installAutoPage(instance: ETS): void {
  if (installed || typeof window === "undefined" || typeof history === "undefined") return;
  installed = true;

  const firePage = () => {
    // Microtask para o URL (location.href) refletir o novo estado antes de
    // coletar context. Try/catch dentro do microtask porque queueMicrotask
    // retorna sincronamente — wrapper externo não captura erros assíncronos.
    queueMicrotask(() => {
      try {
        instance.page();
      } catch {
        // Isola: falha no page() não deve quebrar navegação do site.
      }
    });
  };

  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;

  const patchedPush: HistoryMethod = function (data, unused, url) {
    origPushState.call(history, data, unused, url);
    firePage();
  };
  const patchedReplace: HistoryMethod = function (data, unused, url) {
    origReplaceState.call(history, data, unused, url);
    firePage();
  };

  history.pushState = patchedPush;
  history.replaceState = patchedReplace;

  window.addEventListener("popstate", firePage);
  window.addEventListener("hashchange", firePage);
}
